import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { trackLegalPageViewed } from "@/lib/analytics";

const Privacy = () => {
  const navigate = useNavigate();

  useEffect(() => {
    trackLegalPageViewed('privacy');
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Button onClick={() => navigate(-1)} variant="ghost" className="mb-6">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>

        <Card className="p-8">
          <h1 className="text-4xl font-bold mb-2">Privacy Policy</h1>
          <p className="text-muted-foreground mb-8">Last updated: {new Date().toLocaleDateString()}</p>

          <div className="space-y-8 text-sm leading-relaxed">
            <section>
              <h2 className="text-2xl font-semibold mb-4">1. Introduction</h2>
              <p className="text-muted-foreground">
                Welcome to Decor Canvas - Interior Decor AI Designer ("we," "our," or "us"). Decor Canvas is a SaaS (Software as a Service) provided by 
                <strong> Athenia AI Labs Private Limited</strong>. We respect your privacy and are committed to protecting your personal data. 
                This privacy policy explains how we collect, use, disclose, and safeguard your information when you use our AI-powered interior design service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">2. Data Controller</h2>
              <p className="text-muted-foreground">
                The data controller responsible for your personal data is:
              </p>
              <div className="bg-muted/30 p-4 rounded-lg mt-2">
                <p className="font-semibold">Athenia AI Labs Private Limited</p>
                <p>Email: hello.dc@atheniaai.com</p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">3. Information We Collect</h2>
              
              <h3 className="text-xl font-semibold mt-6 mb-3">3.1 Personal Information You Provide</h3>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                <li><strong>Account Information:</strong> Email address, full name, password (encrypted)</li>
                <li><strong>Profile Information:</strong> Display name, preferences, account settings</li>
                <li><strong>Payment Information:</strong> Processed securely through Dodo Payments or Apple In-App Purchase (we do not store card details)</li>
                <li><strong>Design Content:</strong> Room photos, floor plans, design preferences you upload</li>
                <li><strong>Communication Data:</strong> Messages, support requests, feedback</li>
              </ul>

              <h3 className="text-xl font-semibold mt-6 mb-3">3.2 Automatically Collected Information</h3>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                <li><strong>Technical Data:</strong> IP address (retained for 10 days), browser type, device information, operating system</li>
                <li><strong>Usage Data:</strong> Pages visited, features used, designs generated, time spent on service</li>
                <li><strong>Location Data:</strong> Approximate location (country/region) based on IP address</li>
                <li><strong>Cookies:</strong> Session cookies, preference cookies, analytics cookies (with consent)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">4. How We Use Your Information</h2>
              <p className="text-muted-foreground mb-3">
                We process your personal data for the following purposes, based on the legal grounds specified:
              </p>
              
              <div className="space-y-4">
                <div className="bg-muted/30 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2">Service Delivery (Contractual Necessity)</h4>
                  <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                    <li>Providing AI-powered interior design generation</li>
                    <li>Managing your account and authentication</li>
                    <li>Processing payments and managing subscriptions</li>
                    <li>Storing and managing your design projects</li>
                  </ul>
                </div>

                <div className="bg-muted/30 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2">Service Improvement (Legitimate Interest)</h4>
                  <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                    <li>Analyzing usage patterns to improve our AI models</li>
                    <li>Enhancing user experience and features</li>
                    <li>Detecting and preventing fraud or abuse</li>
                    <li>Ensuring platform security and stability</li>
                  </ul>
                </div>

                <div className="bg-muted/30 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2">Analytics (Consent)</h4>
                  <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                    <li>Google Analytics for understanding user behavior</li>
                    <li>Performance monitoring and optimization</li>
                  </ul>
                </div>

                <div className="bg-muted/30 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2">Legal Compliance (Legal Obligation)</h4>
                  <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                    <li>Responding to legal requests and court orders</li>
                    <li>Complying with tax and accounting regulations</li>
                    <li>Enforcing our Terms of Service</li>
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">5. Data Retention</h2>
              <p className="text-muted-foreground mb-3">We retain your data for the following periods:</p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                <li><strong>Account Data:</strong> Until you delete your account, plus 30 days for recovery</li>
                <li><strong>Design Projects:</strong> Until manually deleted or account closure</li>
                <li><strong>IP Addresses:</strong> 10 days from collection</li>
                <li><strong>User Agent Strings:</strong> 10 days from collection</li>
                <li><strong>Payment Records:</strong> 7 years for tax/accounting compliance</li>
                <li><strong>Analytics Data:</strong> 26 months (Google Analytics default)</li>
                <li><strong>Support Communications:</strong> 3 years from last interaction</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">6. Third-Party Services</h2>
              <p className="text-muted-foreground mb-3">We share data with the following service providers:</p>
              
              <div className="space-y-3">
                <div className="bg-muted/30 p-4 rounded-lg">
                  <h4 className="font-semibold">Google Analytics</h4>
                  <p className="text-sm text-muted-foreground">Analytics and usage tracking (with your consent)</p>
                  <a href="https://policies.google.com/privacy" className="text-primary text-sm hover:underline">Privacy Policy</a>
                </div>

                <div className="bg-muted/30 p-4 rounded-lg">
                  <h4 className="font-semibold">Apple In-App Purchase</h4>
                  <p className="text-sm text-muted-foreground">Payment processing for iOS</p>
                  <a href="https://www.apple.com/legal/privacy/" className="text-primary text-sm hover:underline">Privacy Policy</a>
                </div>

                <div className="bg-muted/30 p-4 rounded-lg">
                  <h4 className="font-semibold">Dodo Payments</h4>
                  <p className="text-sm text-muted-foreground">Payment processing for Web and Android</p>
                  <a href="https://dodopayments.com/privacy-policy" className="text-primary text-sm hover:underline">Privacy Policy</a>
                </div>

                <div className="bg-muted/30 p-4 rounded-lg">
                  <h4 className="font-semibold">Supabase</h4>
                  <p className="text-sm text-muted-foreground">Database and authentication infrastructure</p>
                  <a href="https://supabase.com/privacy" className="text-primary text-sm hover:underline">Privacy Policy</a>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">7. International Data Transfers</h2>
              <p className="text-muted-foreground">
                Your data may be transferred to and processed in the United States and other countries where our service providers operate. 
                We ensure appropriate safeguards are in place, including Standard Contractual Clauses approved by the European Commission.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">8. Your Rights (GDPR & CCPA)</h2>
              <p className="text-muted-foreground mb-3">You have the following rights regarding your personal data:</p>
              
              <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                <li><strong>Right to Access:</strong> Request a copy of your personal data</li>
                <li><strong>Right to Rectification:</strong> Correct inaccurate or incomplete data</li>
                <li><strong>Right to Erasure:</strong> Request deletion of your personal data</li>
                <li><strong>Right to Restrict Processing:</strong> Limit how we use your data</li>
                <li><strong>Right to Data Portability:</strong> Receive your data in a structured format</li>
                <li><strong>Right to Object:</strong> Object to processing based on legitimate interests</li>
                <li><strong>Right to Withdraw Consent:</strong> Withdraw consent for analytics/marketing at any time</li>
                <li><strong>Right to Lodge a Complaint:</strong> File a complaint with your data protection authority</li>
              </ul>

              <p className="text-muted-foreground mt-4">
                To exercise these rights, visit your <a href="/settings" className="text-primary hover:underline">Account Settings</a> or contact us at hello.dc@atheniaai.com
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">9. Children's Privacy</h2>
              <p className="text-muted-foreground">
                Our service is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. 
                If you believe we have collected information from a child under 13, please contact us immediately.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">10. Security</h2>
              <p className="text-muted-foreground">
                We implement industry-standard security measures including encryption, secure authentication, regular security audits, and access controls. 
                However, no method of transmission over the internet is 100% secure. While we strive to protect your data, we cannot guarantee absolute security.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">11. Changes to This Policy</h2>
              <p className="text-muted-foreground">
                We may update this privacy policy from time to time. We will notify you of material changes by email or prominent notice on our website. 
                Your continued use of the service after changes indicates acceptance of the updated policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">12. Contact Us</h2>
              <p className="text-muted-foreground mb-3">
                For any questions about this privacy policy or to exercise your rights, please contact:
              </p>
              <div className="bg-muted/30 p-4 rounded-lg">
                <p className="font-semibold">Athenia AI Labs Private Limited - Privacy Team</p>
                <p>Email: hello.dc@atheniaai.com</p>
                <p>Response time: Within 30 days</p>
              </div>
            </section>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Privacy;
